// The editor's interface around the canvas: top bar, palette (blocks, sections, layers),
// inspector, code view and notifications. It adapts to the width of its container, not the window.
import { h, icon, debounce } from '../core/dom.js';
import { uiIcons } from './icons.js';
import { allBlocks, blockOf } from '../core/registry.js';
import { Inspector } from './inspector.js';
import { sectionTemplates } from '../sections/index.js';
import uiCss from '../css/ui.css';

const CATEGORIES = ['saved', 'layout', 'basic', 'media', 'components', 'advanced'];
const DEVICES = [
    { id: 'desktop', icon: 'desktop', width: null },
    { id: 'tablet', icon: 'device-tablet', width: 820 },
    { id: 'mobile', icon: 'device-mobile', width: 390 },
];

let stylesInjected = false;

function injectStyles(nonce) {
    if (stylesInjected || document.querySelector('style[data-pageflow]')) return;
    stylesInjected = true;
    const style = document.createElement('style');
    style.setAttribute('data-pageflow', '');
    if (nonce) style.setAttribute('nonce', nonce);
    style.textContent = uiCss;
    document.head.append(style);
}

/** Palette entries: blocks and their presets, filtered and ordered by the `blocks` option. */
export function paletteItems(options) {
    const items = [];
    for (const def of allBlocks()) {
        if (def.palette !== false && def.name !== 'col') items.push({ id: def.name, block: def.name, label: def.label, icon: def.icon, category: def.category });
        for (const preset of def.presets || []) {
            items.push({ id: preset.id, block: def.name, label: preset.label, icon: preset.icon || def.icon, category: preset.category || def.category, html: preset.html });
        }
    }
    if (Array.isArray(options.blocks)) {
        const order = options.blocks;
        return order.map(id => items.find(item => item.id === id)).filter(Boolean);
    }
    // Layout first: the container, then the column presets.
    const rank = id => (id === 'container' ? 0 : id === 'container-fluid' ? 1 : id.startsWith('cols-') ? 2 : 3);
    return items.sort((a, b) => CATEGORIES.indexOf(a.category) - CATEGORIES.indexOf(b.category) || rank(a.id) - rank(b.id));
}

export class App {
    constructor(editor) {
        this.editor = editor;
        const o = editor.options;
        const t = editor.t;
        if (o.injectStyles !== false) injectStyles(o.nonce);

        this.el = h('div', {
            class: 'pf-app',
            dataset: { mode: o.mode, theme: o.theme === 'auto' ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : o.theme },
        });
        if (o.height) this.el.style.setProperty('--pf-height', typeof o.height === 'number' ? `${o.height}px` : o.height);
        this.topbar = this.buildTopbar();
        // Plain divs: hosts often style main, aside or nav (main { margin: 0 auto } collapsed the stage).
        this.left = h('div', { class: 'pf-left', role: 'complementary', 'aria-label': t('panel.insert') });
        this.right = h('div', { class: 'pf-right', role: 'complementary', 'aria-label': t('panel.settings') });
        this.stage = h('div', { class: 'pf-device' });
        this.stageWrap = h('div', { class: 'pf-stage' }, this.stage, h('div', { class: 'pf-loading' }, t('canvas.loading')));
        this.toasts = h('div', { class: 'pf-toasts', role: 'status', 'aria-live': 'polite' });
        this.scrim = h('div', { class: 'pf-scrim', onclick: () => this.closeDrawers() });
        this.main = h('div', { class: 'pf-main' }, this.left, this.stageWrap, this.right, this.scrim);
        this.el.append(this.topbar, this.main, this.toasts);
        editor.container.append(this.el);
        // The editor often sits in a form: Enter in one of its fields must never send that form.
        this.el.addEventListener('keydown', event => {
            const target = event.target;
            if (event.key !== 'Enter' || target.localName !== 'input' || /^(checkbox|radio|button|submit|color|range)$/.test(target.type)) return;
            // No implicit submission; leaving the field fires its "change" once, which applies the value.
            event.preventDefault();
            target.blur();
        });

        this.inspector = new Inspector(editor, this.right);
        this.buildLeft();
        this.inspector.render();
        this.refreshLayers = debounce(() => this.renderLayers(), 200);
        this.onResize = () => this.layoutChanged();
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.el);
    }

    // ---- Top bar ---------------------------------------------------------------------------------------

    toolButton(name, iconName, label, onClick, extra = {}) {
        const btn = h('button', { type: 'button', class: 'pf-tool', title: label, 'aria-label': label, dataset: { tool: name }, ...extra }, icon(uiIcons[iconName]));
        btn.addEventListener('click', onClick);
        return btn;
    }

    buildTopbar() {
        const e = this.editor;
        const t = e.t;
        const tb = e.options.toolbar;
        this.tools = {};
        const add = (name, iconName, label, onClick, extra) => {
            this.tools[name] = this.toolButton(name, iconName, label, onClick, extra);
            return this.tools[name];
        };
        const start = h('div', { class: 'pf-bar-group' },
            add('insert', 'plus', t('panel.insert'), () => this.panelButton('left'), { 'aria-expanded': 'false' }),
            tb.undo !== false ? add('undo', 'arrow-counter-clockwise', `${t('action.undo')} (Ctrl+Z)`, () => e.undo()) : null,
            tb.undo !== false ? add('redo', 'arrow-clockwise', `${t('action.redo')} (Ctrl+Y)`, () => e.redo()) : null);
        const devices = e.options.devices !== false && tb.devices !== false
            ? h('div', { class: 'pf-bar-group pf-devices', role: 'radiogroup', 'aria-label': t('device.label') },
                DEVICES.map(device => add(`device-${device.id}`, device.icon, t(`device.${device.id}`), () => e.setDevice(device.id), { role: 'radio' })))
            : h('div', { class: 'pf-bar-group' });
        const end = h('div', { class: 'pf-bar-group' },
            tb.guides !== false ? add('guides', 'frame-corners', t('action.guides'), () => this.toggleGuides(), { 'aria-pressed': 'true' }) : null,
            tb.preview !== false ? add('preview', 'eye', t('action.preview'), () => e.setPreview(!e.preview), { 'aria-pressed': 'false' }) : null,
            tb.code !== false ? add('code', 'code', t('action.code'), () => this.openCode()) : null,
            tb.copy !== false ? add('copy', 'clipboard-text', t('action.copyCode'), () => this.copyCode()) : null,
            tb.fullscreen !== false ? add('fullscreen', 'corners-out', t('action.fullscreen'), () => this.toggleFullscreen(), { 'aria-pressed': 'false' }) : null,
            add('settings', 'sliders-horizontal', t('panel.settings'), () => this.panelButton('right'), { 'aria-expanded': 'false' }),
            ...(e.options.actions || []).map(action => {
                const btn = h('button', { type: 'button', class: 'pf-action' + (action.primary ? ' pf-action-primary' : '') }, action.icon && uiIcons[action.icon] ? icon(uiIcons[action.icon]) : null, h('span', {}, action.label));
                btn.addEventListener('click', () => action.onClick && action.onClick(e));
                return btn;
            }));
        return h('div', { class: 'pf-topbar', role: 'toolbar', 'aria-label': 'PageFlow' }, start, devices, end);
    }

    // ---- Left panel: blocks, sections, layers ------------------------------------------------------------

    buildLeft() {
        const e = this.editor;
        const t = e.t;
        const tabs = [['blocks', 'squares-four', t('tab.blocks')]];
        if (e.options.sections !== false) tabs.push(['sections', 'layout', t('tab.sections')]);
        if (e.options.toolbar.layers !== false) tabs.push(['layers', 'stack', t('tab.layers')]);
        // Medium containers: the settings share this panel (shown by the tab only then).
        tabs.push(['settings', 'sliders-horizontal', t('panel.settings')]);
        this.tabButtons = {};
        this.tabPanels = {};
        const list = h('div', { class: 'pf-tabs', role: 'tablist' });
        tabs.forEach(([id, iconName, label]) => {
            const btn = h('button', { type: 'button', class: 'pf-tab', role: 'tab', id: `pf-tab-${id}`, 'aria-selected': 'false', dataset: { tab: id } }, icon(uiIcons[iconName]), h('span', {}, label));
            btn.addEventListener('click', () => this.showTab(id));
            this.tabButtons[id] = btn;
            list.append(btn);
            this.tabPanels[id] = h('div', { class: 'pf-tab-panel', role: 'tabpanel', 'aria-labelledby': `pf-tab-${id}`, hidden: true, dataset: { tab: id } });
        });
        this.left.append(list, ...Object.values(this.tabPanels));
        this.renderPalette();
        if (this.tabPanels.sections) this.renderSections();
        this.showTab('blocks', false);
    }

    showTab(id, openDrawer = true) {
        if (!this.tabPanels[id]) return;
        Object.entries(this.tabPanels).forEach(([key, panel]) => {
            panel.hidden = key !== id;
            this.tabButtons[key].setAttribute('aria-selected', key === id ? 'true' : 'false');
            this.tabButtons[key].classList.toggle('pf-on', key === id);
        });
        this.currentTab = id;
        if (id === 'layers') this.renderLayers();
        if (openDrawer && this.isNarrow()) this.openDrawer('left');
        if (this.layout === 'medium') this.el.classList.remove('pf-hide-left');
        this.tools.settings.classList.toggle('pf-on', this.layout === 'medium' && id === 'settings');
        this.tools.insert.classList.toggle('pf-on', this.layout === 'medium' ? id !== 'settings' && !this.el.classList.contains('pf-hide-left') : this.tools.insert.classList.contains('pf-on'));
    }

    /** Pointer down on a palette entry: drag it into the canvas, or click to insert it. */
    bindDrag(btn, item) {
        btn.addEventListener('pointerdown', event => {
            if (event.button !== 0 || !this.editor.dnd) return;
            event.preventDefault();
            if (event.pointerType === 'touch') {
                // On touch screens a tap inserts; dragging would fight with scrolling the palette.
                btn.addEventListener('pointerup', () => {
                    this.editor.insertItem(item);
                    this.closeDrawers();
                }, { once: true });
                return;
            }
            this.editor.dnd.startNew(item, event, btn);
        });
        btn.addEventListener('keydown', event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.editor.insertItem(item);
            }
        });
    }

    renderPalette() {
        const e = this.editor;
        const t = e.t;
        const panel = this.tabPanels.blocks;
        const search = h('input', { type: 'search', class: 'pf-input pf-search', placeholder: t('action.searchBlocks'), 'aria-label': t('action.searchBlocks') });
        const groups = h('div', { class: 'pf-palette' });
        const items = paletteItems(e.options);
        this.drawPalette = async () => {
            const saved = (await e.savedBlocks()).map(item => ({ ...item, category: 'saved', icon: 'bookmark-simple', saved: true }));
            this.paletteEntries = [...saved, ...items];
            draw();
        };
        const draw = () => {
            const query = search.value.trim().toLowerCase();
            groups.replaceChildren();
            for (const category of CATEGORIES) {
                const entries = (this.paletteEntries || items).filter(item => item.category === category && (!query || t(item.label).toLowerCase().includes(query) || item.id.includes(query)));
                if (!entries.length) continue;
                const grid = h('div', { class: 'pf-palette-grid' });
                entries.forEach(item => {
                    const btn = h('button', { type: 'button', class: 'pf-block-item', title: t('palette.hint'), dataset: { item: item.id } }, icon(uiIcons[item.icon] || ''), h('span', {}, item.saved ? item.label : t(item.label)));
                    this.bindDrag(btn, item);
                    if (item.saved) {
                        const remove = h('span', { class: 'pf-item-remove', role: 'button', tabindex: '0', title: t('saved.remove'), 'aria-label': t('saved.remove') }, icon(uiIcons.x));
                        const removeItem = event => {
                            event.preventDefault();
                            event.stopPropagation();
                            e.removeSavedBlock(item.id);
                        };
                        remove.addEventListener('pointerdown', event => event.stopPropagation());
                        remove.addEventListener('click', removeItem);
                        remove.addEventListener('keydown', event => (event.key === 'Enter' || event.key === ' ') && removeItem(event));
                        btn.classList.add('pf-block-saved');
                        btn.append(remove);
                    }
                    grid.append(btn);
                });
                groups.append(h('div', { class: 'pf-palette-group' }, h('div', { class: 'pf-group-title' }, t(`category.${category}`)), grid));
            }
            if (!groups.children.length) groups.append(h('p', { class: 'pf-muted' }, t('palette.none')));
        };
        search.addEventListener('input', draw);
        draw();
        this.drawPalette();
        panel.append(search, groups);
    }

    renderSections() {
        const e = this.editor;
        const t = e.t;
        const panel = this.tabPanels.sections;
        const templates = Array.isArray(e.options.sections) ? e.options.sections : sectionTemplates;
        const list = h('div', { class: 'pf-sections' });
        templates.forEach(section => {
            const item = { id: section.id, block: section.block || 'box', label: section.label, icon: section.icon || 'layout', html: section.html };
            const btn = h('button', { type: 'button', class: 'pf-section-item', dataset: { item: section.id } },
                h('span', { class: 'pf-section-thumb', 'aria-hidden': 'true', html: section.thumb || '' }),
                h('span', { class: 'pf-section-text' }, h('strong', {}, t(section.label)), section.description ? h('small', {}, t(section.description)) : null));
            this.bindDrag(btn, item);
            list.append(btn);
        });
        panel.append(h('p', { class: 'pf-muted pf-panel-intro' }, t('sections.intro')), list);
    }

    renderLayers() {
        const panel = this.tabPanels.layers;
        if (!panel || panel.hidden || !this.editor.canvas) return;
        const e = this.editor;
        const t = e.t;
        const tree = h('ul', { class: 'pf-layers', role: 'tree' });
        const walk = (parent, list, depth) => {
            for (const child of parent.children) {
                if (!child.hasAttribute('data-pf')) {
                    walk(child, list, depth);
                    continue;
                }
                const def = blockOf(child);
                if (!def) continue;
                const text = (child.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 32);
                const item = h('li', { role: 'treeitem' });
                const btn = h('button', { type: 'button', class: 'pf-layer' + (child === e.selected ? ' pf-on' : ''), style: { paddingLeft: `${8 + depth * 14}px` } },
                    icon(uiIcons[def.icon] || ''), h('span', { class: 'pf-layer-name' }, t(def.label)), text && def.kind === 'leaf' ? h('span', { class: 'pf-layer-text' }, text) : null);
                btn.addEventListener('click', () => {
                    e.select(child);
                    e.reveal(child, true);
                });
                btn.addEventListener('mouseenter', () => e.overlay.setHover(child));
                btn.addEventListener('mouseleave', () => e.overlay.setHover(null));
                item.append(btn);
                list.append(item);
                if (def.kind !== 'leaf') {
                    const sub = h('ul', { role: 'group' });
                    walk(child, sub, depth + 1);
                    if (sub.children.length) item.append(sub);
                }
            }
        };
        walk(e.canvas.root, tree, 0);
        panel.replaceChildren(tree.children.length ? tree : h('p', { class: 'pf-muted pf-panel-intro' }, t('layers.empty')));
    }

    // ---- Code view -----------------------------------------------------------------------------------

    openCode() {
        const e = this.editor;
        const t = e.t;
        e.flush();
        const textarea = h('textarea', { class: 'pf-code-editor', spellcheck: 'false', 'aria-label': t('action.code') }, e.getHTML());
        const close = () => {
            dialog.close();
            dialog.remove();
        };
        const apply = h('button', { type: 'button', class: 'pf-btn pf-btn-primary' }, t('action.apply'));
        apply.addEventListener('click', () => {
            e.setHTML(textarea.value);
            close();
            this.toast(t('notice.codeApplied'), 'success');
        });
        const copy = h('button', { type: 'button', class: 'pf-btn' }, icon(uiIcons['clipboard-text']), t('action.copyCode'));
        copy.addEventListener('click', () => this.copyText(textarea.value));
        const cancel = h('button', { type: 'button', class: 'pf-btn' }, t('action.cancel'));
        cancel.addEventListener('click', close);
        const dialog = h('dialog', { class: 'pf-dialog', 'aria-label': t('action.code') },
            h('div', { class: 'pf-dialog-head' }, h('strong', {}, t('code.title')), this.toolButton('close', 'x', t('action.close'), close)),
            h('p', { class: 'pf-muted' }, t('code.help')),
            textarea,
            h('div', { class: 'pf-dialog-actions' }, copy, h('span', { class: 'pf-spacer' }), cancel, apply));
        dialog.addEventListener('cancel', event => {
            event.preventDefault();
            close();
        });
        textarea.addEventListener('keydown', event => {
            if (event.key === 'Tab') {
                event.preventDefault();
                textarea.setRangeText('    ', textarea.selectionStart, textarea.selectionEnd, 'end');
            }
            event.stopPropagation();
        });
        this.el.append(dialog);
        dialog.showModal();
        textarea.focus();
    }

    async copyText(text) {
        const t = this.editor.t;
        try {
            if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
            else {
                const area = h('textarea', { style: 'position:fixed;opacity:0' }, text);
                document.body.append(area);
                area.select();
                document.execCommand('copy');
                area.remove();
            }
            this.toast(t('notice.copied'), 'success');
        } catch (error) {
            this.toast(t('error.copy'), 'error');
        }
    }

    copyCode() {
        this.editor.flush();
        this.copyText(this.editor.getHTML());
    }

    // ---- States ----------------------------------------------------------------------------------------

    ready() {
        this.el.classList.add('pf-ready');
        // On a small screen the page is edited as a phone shows it, at a readable size.
        const initial = this.editor.options.device;
        if (initial && DEVICES.some(device => device.id === initial)) this.editor.device = initial;
        else if (this.el.clientWidth < 700) this.editor.device = 'mobile';
        this.deviceChanged();
        this.historyChanged();
        this.renderLayers();
        if (this.editor.options.autofocus) this.editor.canvas.iframe.focus();
    }

    selectionChanged() {
        this.inspector.render();
        if (this.currentTab === 'layers') this.refreshLayers();
        if (this.isNarrow() && this.editor.selected) this.closeDrawer('left');
        // One panel for everything: a selected block shows its settings, none brings the blocks back.
        if (this.layout === 'medium' && this.currentTab !== 'layers' && !(this.editor.dnd && this.editor.dnd.active)) {
            if (this.editor.selected) this.showTab('settings', false);
            else if (this.currentTab === 'settings') this.showTab('blocks', false);
        }
    }

    historyChanged() {
        const history = this.editor.history;
        const pending = this.editor.canvas ? this.editor.hasPendingChanges() : false;
        if (this.tools.undo) this.tools.undo.disabled = !history.canUndo() && !pending;
        if (this.tools.redo) this.tools.redo.disabled = !history.canRedo() || pending;
    }

    contentChanged() {
        if (this.currentTab === 'layers') this.refreshLayers();
    }

    /**
     * Sizes the canvas: a device keeps its real width (desktop: at least `desktopWidth`, 1024 px) so that
     * Bootstrap sees the right breakpoint, and the whole device is scaled down when the stage is narrower.
     * (1024 px is Bootstrap's "lg": the desktop layout, still readable once scaled in a narrow stage.)
     */
    fitStage() {
        const device = DEVICES.find(item => item.id === this.editor.device) || DEVICES[0];
        const wrap = this.stageWrap;
        const style = getComputedStyle(wrap);
        const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        const availableWidth = Math.max(200, wrap.clientWidth - padX);
        const availableHeight = Math.max(200, wrap.clientHeight - padY);
        this.stage.dataset.device = device.id;
        const border = device.width ? parseFloat(getComputedStyle(this.stage).borderLeftWidth) || 0 : 0;
        const minDesktop = this.editor.options.desktopWidth || 1024;
        const width = device.width || Math.max(availableWidth, minDesktop);
        const outer = width + border * 2;
        const scale = Math.min(1, availableWidth / outer);
        this.scale = scale;
        this.stage.style.width = `${width}px`;
        this.stage.style.height = `${Math.floor(availableHeight / scale) - border * 2}px`;
        this.stage.style.left = `${Math.round(parseFloat(style.paddingLeft) + (availableWidth - outer * scale) / 2)}px`;
        this.stage.style.top = `${Math.round(parseFloat(style.paddingTop))}px`;
        this.stage.style.transform = scale < 1 ? `scale(${scale})` : '';
        // The editing tools inside the canvas keep their real size on screen.
        if (this.editor.canvas) this.editor.canvas.doc.documentElement.style.setProperty('--pf-k', String(Math.min(1.8, 1 / scale)));
        if (this.editor.overlay) this.editor.overlay.schedule();
    }

    deviceChanged() {
        const device = DEVICES.find(item => item.id === this.editor.device) || DEVICES[0];
        this.fitStage();
        DEVICES.forEach(item => {
            const btn = this.tools[`device-${item.id}`];
            if (btn) {
                btn.classList.toggle('pf-on', item.id === device.id);
                btn.setAttribute('aria-checked', item.id === device.id ? 'true' : 'false');
            }
        });
        // The column sizes shown by the inspector depend on the breakpoint of the canvas.
        setTimeout(() => {
            if (this.editor.overlay) this.editor.overlay.schedule();
            this.inspector.render();
        }, 260);
    }

    previewChanged() {
        const on = this.editor.preview;
        this.el.classList.toggle('pf-previewing', on);
        if (this.tools.preview) {
            this.tools.preview.classList.toggle('pf-on', on);
            this.tools.preview.setAttribute('aria-pressed', on ? 'true' : 'false');
            this.tools.preview.replaceChildren(icon(uiIcons[on ? 'eye-slash' : 'eye']));
        }
        this.fitStage();
    }

    toggleGuides() {
        const body = this.editor.canvas.doc.body;
        const off = body.classList.toggle('pf-no-guides');
        this.tools.guides.classList.toggle('pf-on', !off);
        this.tools.guides.setAttribute('aria-pressed', off ? 'false' : 'true');
    }

    toggleFullscreen() {
        const on = this.el.classList.toggle('pf-fullscreen');
        document.documentElement.classList.toggle('pf-has-fullscreen', on);
        this.tools.fullscreen.classList.toggle('pf-on', on);
        this.tools.fullscreen.setAttribute('aria-pressed', on ? 'true' : 'false');
        this.tools.fullscreen.replaceChildren(icon(uiIcons[on ? 'corners-in' : 'corners-out']));
        this.fitStage();
    }

    // ---- Layouts, from the width of the container -------------------------------------------------------
    // wide (1500 px and more): blocks on the left, settings on the right;
    // medium: one panel with tabs, the settings among them, so that the canvas keeps its room;
    // narrow (under 900 px): both panels become drawers over the canvas.

    isNarrow() {
        return this.layout === 'narrow';
    }

    layoutChanged() {
        const width = this.el.clientWidth;
        const layout = width >= 1500 ? 'wide' : width >= 900 ? 'medium' : 'narrow';
        const previous = this.layout;
        this.layout = layout;
        this.el.classList.toggle('pf-wide', layout === 'wide');
        this.el.classList.toggle('pf-medium', layout === 'medium');
        this.el.classList.toggle('pf-narrow', layout === 'narrow');
        this.el.classList.toggle('pf-tiny', width < 640);
        if (layout !== previous) {
            if (layout === 'medium') this.tabPanels.settings.append(this.right);
            else if (this.right.parentNode !== this.main) this.main.insertBefore(this.right, this.scrim);
            if (layout !== 'narrow') this.closeDrawers();
            if (layout !== 'medium' && this.currentTab === 'settings') this.showTab('blocks', false);
            if (layout === 'medium' && this.editor.selected) this.showTab('settings', false);
            this.el.classList.remove('pf-hide-left', 'pf-hide-right');
        }
        this.tools.insert.classList.toggle('pf-on', layout !== 'narrow' && !this.el.classList.contains('pf-hide-left') && !(layout === 'medium' && this.currentTab === 'settings'));
        this.tools.settings.classList.toggle('pf-on', layout === 'wide' ? !this.el.classList.contains('pf-hide-right') : layout === 'medium' && this.currentTab === 'settings');
        this.fitStage();
    }

    /** The "add" and "settings" buttons of the top bar, for each layout. */
    panelButton(side) {
        if (this.layout === 'medium') {
            const tab = side === 'right' ? 'settings' : (this.currentTab && this.currentTab !== 'settings' ? this.currentTab : 'blocks');
            // A second click on the active button hides the panel, to give all the room to the canvas.
            const active = !this.el.classList.contains('pf-hide-left') && (side === 'right' ? this.currentTab === 'settings' : this.currentTab !== 'settings');
            if (active) {
                this.el.classList.add('pf-hide-left');
                this.tools.insert.classList.remove('pf-on');
                this.tools.settings.classList.remove('pf-on');
            } else {
                this.el.classList.remove('pf-hide-left');
                this.showTab(tab, false);
            }
            this.fitStage();
            return;
        }
        this.toggleDrawer(side);
    }

    openDrawer(side) {
        if (!this.isNarrow()) return;
        this.el.classList.add(`pf-open-${side}`);
        this.el.classList.remove(`pf-open-${side === 'left' ? 'right' : 'left'}`);
        this.tools.insert.setAttribute('aria-expanded', side === 'left' ? 'true' : 'false');
        this.tools.settings.setAttribute('aria-expanded', side === 'right' ? 'true' : 'false');
    }

    closeDrawer(side) {
        this.el.classList.remove(`pf-open-${side}`);
        this.tools[side === 'left' ? 'insert' : 'settings'].setAttribute('aria-expanded', 'false');
    }

    closeDrawers() {
        this.closeDrawer('left');
        this.closeDrawer('right');
    }

    /** On a wide container the buttons show or hide a side panel; on a narrow one they open a drawer. */
    toggleDrawer(side) {
        if (!this.isNarrow()) {
            const hidden = this.el.classList.toggle(`pf-hide-${side}`);
            this.tools[side === 'left' ? 'insert' : 'settings'].classList.toggle('pf-on', !hidden);
            this.fitStage();
            return;
        }
        if (this.el.classList.contains(`pf-open-${side}`)) this.closeDrawer(side);
        else this.openDrawer(side);
    }

    toast(message, type = 'info', duration = 3000, action = null) {
        const node = h('div', { class: `pf-toast pf-toast-${type}` + (action ? ' pf-toast-action' : '') }, h('span', {}, message));
        const close = () => {
            node.classList.add('pf-out');
            setTimeout(() => node.remove(), 300);
        };
        if (action) {
            const btn = h('button', { type: 'button', class: 'pf-toast-btn' }, action.label);
            btn.addEventListener('click', () => {
                action.onClick();
                close();
            });
            node.append(btn, h('button', { type: 'button', class: 'pf-toast-close', 'aria-label': this.editor.t('action.close') }, icon(uiIcons.x)));
            node.lastChild.addEventListener('click', close);
        }
        this.toasts.append(node);
        setTimeout(close, duration);
    }

    savedChanged() {
        if (this.drawPalette) this.drawPalette();
    }

    /** Asks the name of a reusable block, then saves the selected block. */
    askSaveBlock() {
        const e = this.editor;
        const t = e.t;
        const el = e.selected;
        if (!el) return;
        const input = h('input', { type: 'text', class: 'pf-input', value: t(blockOf(el).label), maxlength: 60, 'aria-label': t('saved.name') });
        const close = () => {
            dialog.close();
            dialog.remove();
        };
        const save = h('button', { type: 'button', class: 'pf-btn pf-btn-primary' }, t('action.save'));
        // No <form> here: it would be nested in the host's form and its submit event would reach it.
        const form = h('div', { class: 'pf-dialog-form' },
            h('div', { class: 'pf-dialog-head' }, h('strong', {}, t('action.saveBlock')), this.toolButton('close', 'x', t('action.close'), close)),
            h('p', { class: 'pf-muted' }, t('saved.help')),
            h('label', { class: 'pf-label' }, t('saved.name')),
            input,
            h('div', { class: 'pf-dialog-actions' }, h('span', { class: 'pf-spacer' }), h('button', { type: 'button', class: 'pf-btn', onclick: close }, t('action.cancel')), save));
        const submit = () => {
            const name = input.value.trim();
            if (!name) {
                input.classList.add('pf-invalid');
                return;
            }
            e.saveBlock(el, name);
            close();
        };
        save.addEventListener('click', submit);
        input.addEventListener('keydown', event => {
            if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                submit();
            }
        });
        const dialog = h('dialog', { class: 'pf-dialog pf-dialog-small' }, form);
        dialog.addEventListener('keydown', event => event.stopPropagation());
        this.el.append(dialog);
        dialog.showModal();
        input.select();
    }

    destroy() {
        this.resizeObserver.disconnect();
        document.documentElement.classList.remove('pf-has-fullscreen');
        this.el.remove();
    }
}
